import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Phone, MapPin, Clock, Menu as MenuIcon, X, Plus, Minus } from 'lucide-react';
import { mockData } from '../data/mock';
import { useCart } from './CartContext';
import PerfectoCustomizationModal from './PerfectoCustomizationModal';
import PromoModal from './PromoModal';
import TexMexModal from './TexMexModal';

const PerfectoHome = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pizzaTomateOpen, setPizzaTomateOpen] = useState(false);
  const [pizzaCremeOpen, setPizzaCremeOpen] = useState(false);
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [selectedPromoType, setSelectedPromoType] = useState(null);
  const [texMexModalOpen, setTexMexModalOpen] = useState(false);
  const { addItem, items, updateQuantity, activePromotion } = useCart();

  // Update time every 30 seconds for real-time status
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const openLightbox = (imageSrc) => {
    setLightboxImage(imageSrc);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const handlePhoneCall = () => {
    window.open('tel:0169191505');
  };

  // Fonction pour extraire le nom de catégorie sans emojis et nettoyer
  const getCategoryPrefix = (categoryName) => {
    if (!categoryName) return '';
    
    // Retirer les emojis et les textes comme "BASE SAUCE TOMATE"
    let cleanName = categoryName
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Supprimer les emojis
      .replace(/BASE SAUCE TOMATE|BASE CRÈME FRAÎCHE/gi, '') // Supprimer les bases
      .replace(/PIZZAS?/gi, 'Pizza') // Normaliser PIZZAS → Pizza
      .replace(/MENUS?/gi, 'Menu')
      .replace(/SANDWICHS?/gi, 'Sandwich')
      .replace(/BURGERS?/gi, 'Burger')
      .replace(/RAVIOLIS?/gi, 'Raviolis')
      .replace(/PÂTES?/gi, 'Pâtes')
      .replace(/PANINIS?/gi, 'Panini')
      .replace(/SALADES?/gi, 'Salade')
      .replace(/TACOS?/gi, 'Tacos')
      .replace(/ZAPDWICHS?/gi, 'Zapdwich')
      .replace(/DESSERTS?/gi, 'Dessert')
      .replace(/GLACES?/gi, 'Glace')
      .replace(/BOISSONS?/gi, 'Boisson')
      .trim();
    
    // Si la catégorie contient MIDIS ou BAMBINO, extraire juste "Menu"
    if (categoryName.includes('MIDIS') || categoryName.includes('BAMBINO')) {
      return 'Menu';
    }
    
    return cleanName || 'Produit';
  };

  // Fonction pour préfixer un nom avec sa catégorie
  const addCategoryPrefix = (itemName, categoryName) => {
    const prefix = getCategoryPrefix(categoryName);
    
    // Ne pas ajouter de préfixe si l'item commence déjà par un type similaire
    if (itemName.toLowerCase().startsWith(prefix.toLowerCase())) {
      return itemName;
    }
    
    return `${prefix} – ${itemName}`;
  };

  const handleAddToCart = (item, categoryName) => {
    const category = mockData.menuCategories.find(cat => cat.name === categoryName);
    
    // Special handling for TEX MEX
    if (categoryName.includes('TEX MEX')) {
      // MUX is fixed at 12 pieces - add directly
      if (item.fixedSize === true) {
        const prefixedName = addCategoryPrefix(item.name, categoryName);
        const cartItem = {
          id: `${categoryName}-${item.name}`.replace(/\s+/g, '-').toLowerCase(),
          name: `${prefixedName} - 12 pièces`,
          price: item.price,
          description: item.description,
          category: categoryName
        };
        addItem(cartItem);
      } else {
        // Other TEX MEX items need size selection
        setSelectedItem(item);
        setSelectedCategory(category);
        setTexMexModalOpen(true);
      }
      return;
    }
    
    // Exclusions: items that should NOT open customization modal
    const isExcluded = (
      categoryName.includes('Boissons') ||
      categoryName.includes('Desserts') ||
      categoryName.includes('GLACES') ||
      categoryName.includes('PANINIS') ||
      categoryName.includes('PÂTES') ||
      categoryName.includes('RAVIOLIS')
    );
    
    // ALL items need customization EXCEPT excluded categories
    const needsCustomization = !isExcluded;
    
    if (needsCustomization) {
      setSelectedItem(item);
      setSelectedCategory(category);
      setCustomizationModalOpen(true);
    } else {
      // Regular add to cart for excluded items
      const prefixedName = addCategoryPrefix(item.name, categoryName);
      const cartItem = {
        id: `${categoryName}-${item.name}`.replace(/\s+/g, '-').toLowerCase(),
        name: prefixedName,
        price: item.price,
        description: item.description,
        category: categoryName
      };
      addItem(cartItem);
    }
  };

  const handleCustomizedAddToCart = (customizedItem) => {
    addItem(customizedItem);
  };

  const handlePromoClick = (promoType) => {
    // Vérifier si une autre promo est déjà active
    if (activePromotion && activePromotion !== promoType) {
      const promoName = activePromotion === 'promo1_livraison' 
        ? 'Promo Livraison (3ème pizza offerte)'
        : activePromotion === 'promo2_emporter'
          ? 'Promo À Emporter (2ème pizza réduite)'
          : 'Promo (2ème pizza à -50%)';
      
      alert(`⚠️ Une promotion est déjà active dans votre panier : ${promoName}.\n\nLes promotions ne sont pas cumulables. Veuillez vider votre panier pour profiter d'une autre promotion.`);
      return;
    }
    
    setSelectedPromoType(promoType);
    setPromoModalOpen(true);
  };

  const handlePromoAddToCart = (item) => {
    addItem(item);
  };

  const handleRemoveFromCart = (item, categoryName) => {
    const itemId = `${categoryName}-${item.name}`.replace(/\s+/g, '-').toLowerCase();
    const existingItem = items.find(cartItem => cartItem.id === itemId);
    if (existingItem) {
      if (existingItem.quantity > 1) {
        updateQuantity(itemId, existingItem.quantity - 1);
      } else {
        updateQuantity(itemId, 0);
      }
    }
  };

  const getItemQuantity = (item, categoryName) => {
    const itemId = `${categoryName}-${item.name}`.replace(/\s+/g, '-').toLowerCase();
    const existingItem = items.find(cartItem => cartItem.id === itemId);
    return existingItem ? existingItem.quantity : 0;
  };

  return (
    <div className="perfecto-site overflow-x-hidden">
      {/* Navbar */}
      <nav className="navbar sticky top-0 z-50 bg-[#1B1B1B] text-white px-4 py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_woodfiredpizza/artifacts/o98nb6sw_Untitled%20design%20%281%29.png" 
              alt="Perfecto Pizza Logo" 
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover flex-shrink-0"
              style={{ background: 'transparent' }}
            />
            <span className="font-bold text-lg md:text-xl truncate">Perfecto Pizza</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-6">
            <button onClick={() => scrollToSection('hero')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-accueil">Accueil</button>
            <button onClick={() => scrollToSection('menu')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-menu">Menu</button>
            <button onClick={() => scrollToSection('infos')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-infos">Infos</button>
            <button onClick={() => scrollToSection('galerie')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-galerie">Galerie</button>
            <button onClick={() => scrollToSection('apropos')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-apropos">À propos</button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-[#D47A2A] transition-colors" data-testid="nav-contact">Contact</button>
          </div>
          
          {/* Desktop Commander Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Button 
              onClick={handlePhoneCall} 
              className="bg-[#D47A2A] hover:bg-[#B8661F] text-white border-[#D47A2A] font-semibold"
              data-testid="nav-appeler-btn"
            >
              Appeler
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-[#D47A2A] rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#1B1B1B] border-t border-[#D47A2A] shadow-lg" data-testid="mobile-menu">
            <div className="px-4 py-2 space-y-1">
              <button 
                onClick={() => scrollToSection('menu')} 
                className="block w-full text-left px-4 py-3 hover:bg-[#D47A2A] hover:text-white transition-colors rounded-lg"
                data-testid="mobile-nav-menu"
              >
                Menu
              </button>
              <button 
                onClick={() => scrollToSection('infos')} 
                className="block w-full text-left px-4 py-3 hover:bg-[#D47A2A] hover:text-white transition-colors rounded-lg"
                data-testid="mobile-nav-infos"
              >
                Infos
              </button>
              <button 
                onClick={() => scrollToSection('galerie')} 
                className="block w-full text-left px-4 py-3 hover:bg-[#D47A2A] hover:text-white transition-colors rounded-lg"
                data-testid="mobile-nav-galerie"
              >
                Galerie
              </button>
              <button 
                onClick={() => scrollToSection('apropos')} 
                className="block w-full text-left px-4 py-3 hover:bg-[#D47A2A] hover:text-white transition-colors rounded-lg"
                data-testid="mobile-nav-apropos"
              >
                À propos
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="block w-full text-left px-4 py-3 hover:bg-[#D47A2A] hover:text-white transition-colors rounded-lg"
                data-testid="mobile-nav-contact"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1606265698533-c17fc6003934')`,
        filter: 'brightness(0.7) contrast(1.1)'
      }}>
        <div className="hero-content text-center px-4 sm:px-6" data-testid="hero-section">
          <img 
            src="https://customer-assets.emergentagent.com/job_woodfiredpizza/artifacts/o98nb6sw_Untitled%20design%20%281%29.png" 
            alt="Perfecto Pizza Logo" 
            className="h-32 w-32 md:h-40 md:w-40 mx-auto mb-6 rounded-full object-cover shadow-2xl"
            data-testid="hero-logo"
          />
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 leading-tight" data-testid="hero-title">Perfecto Pizza – Palaiseau</h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-6" data-testid="hero-subtitle">Pizzas • Tacos • Burgers • Pâtes</p>
          <div className="bg-[#D47A2A] text-white text-base sm:text-lg px-4 sm:px-6 py-2 mb-8 rounded-full mx-auto max-w-fit" data-testid="hero-slogan">
            {mockData.heroSlogan}
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-2 text-lg sm:text-xl" data-testid="hero-phone">
              <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="text-center">Appeler maintenant • 01 69 19 15 05</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <Button 
                onClick={() => scrollToSection('menu')} 
                className="bg-[#D47A2A] hover:bg-[#B8661F] text-white px-6 sm:px-8 py-3 text-base sm:text-lg w-full sm:w-auto"
                data-testid="hero-voir-menu-btn"
              >
                Voir le menu
              </Button>
              <Button 
                onClick={handlePhoneCall} 
                className="bg-[#D47A2A] hover:bg-[#B8661F] text-white border-[#D47A2A] px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold w-full sm:w-auto"
                data-testid="hero-commander-btn"
              >
                Commander
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="py-8 px-4 sm:px-6" data-testid="promo-banner-section">
        <div className="container mx-auto space-y-4">
          {/* Promo 1 - Livraison */}
          <div className="promo-banner relative" data-testid="promo-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <span>🎉 <span className="text-red-600 font-extrabold">Livraison</span> : 2 pizzas Senior ou Mega achetées = la 3ème offerte (la moins chère, hors Classica).</span>
                {activePromotion === 'promo1_livraison' && (
                  <span className="ml-3 text-sm font-bold bg-white text-[#D47A2A] px-3 py-1 rounded-full">
                    ✓ Active
                  </span>
                )}
              </div>
              <Button 
                onClick={() => handlePromoClick('promo1_livraison')}
                disabled={activePromotion && activePromotion !== 'promo1_livraison'}
                className={`font-bold px-6 py-2 rounded-full shadow-lg transition-all ${
                  activePromotion && activePromotion !== 'promo1_livraison'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                    : 'bg-white text-[#D47A2A] hover:bg-gray-100 promo-button-animated'
                }`}
                data-testid="promo-1-profiter-btn"
              >
                {activePromotion && activePromotion !== 'promo1_livraison' ? 'Non cumulable' : 'Profiter'}
              </Button>
            </div>
          </div>
          
          {/* Promo 2 - À emporter */}
          <div className="promo-banner relative" data-testid="promo-banner-2">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <span>🎉 <span className="text-red-600 font-extrabold">À emporter</span> : 2ème pizza Senior à 2 € • 2ème pizza Mega à 4 € (hors Classica).</span>
                {activePromotion === 'promo2_emporter' && (
                  <span className="ml-3 text-sm font-bold bg-white text-[#D47A2A] px-3 py-1 rounded-full">
                    ✓ Active
                  </span>
                )}
              </div>
              <Button 
                onClick={() => handlePromoClick('promo2_emporter')}
                disabled={activePromotion && activePromotion !== 'promo2_emporter'}
                className={`font-bold px-6 py-2 rounded-full shadow-lg transition-all ${
                  activePromotion && activePromotion !== 'promo2_emporter'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                    : 'bg-white text-[#D47A2A] hover:bg-gray-100 promo-button-animated'
                }`}
                data-testid="promo-2-profiter-btn"
              >
                {activePromotion && activePromotion !== 'promo2_emporter' ? 'Non cumulable' : 'Profiter'}
              </Button>
            </div>
          </div>

          {/* Promo 3 - À emporter ou Livraison (nouvelle promo -50%) */}
          <div className="promo-banner relative" data-testid="promo-banner-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <span>🎉 <span className="text-red-600 font-extrabold">À emporter ou Livraison</span> : 1 pizza achetée = la 2ème à -50 % (valable Senior & Mega, hors Classica).</span>
                {activePromotion === 'promo3_emporter_50' && (
                  <span className="ml-3 text-sm font-bold bg-white text-[#D47A2A] px-3 py-1 rounded-full">
                    ✓ Active
                  </span>
                )}
              </div>
              <Button 
                onClick={() => handlePromoClick('promo3_emporter_50')}
                disabled={activePromotion && activePromotion !== 'promo3_emporter_50'}
                className={`font-bold px-6 py-2 rounded-full shadow-lg transition-all ${
                  activePromotion && activePromotion !== 'promo3_emporter_50'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                    : 'bg-white text-[#D47A2A] hover:bg-gray-100 promo-button-animated'
                }`}
                data-testid="promo-3-profiter-btn"
              >
                {activePromotion && activePromotion !== 'promo3_emporter_50' ? 'Non cumulable' : 'Profiter'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-12 sm:py-16 px-4 sm:px-6" data-testid="menu-section">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-12" data-testid="menu-title">Notre Menu</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.menuCategories.map((category, index) => {
              // Déterminer si c'est une catégorie pizza avec accordion
              const isPizzaTomateCategory = category.name.includes('PIZZAS BASE SAUCE TOMATE');
              const isPizzaCremeCategory = category.name.includes('PIZZAS BASE CRÈME');
              const isCollapsible = isPizzaTomateCategory || isPizzaCremeCategory;
              const isOpen = isPizzaTomateCategory ? pizzaTomateOpen : isPizzaCremeCategory ? pizzaCremeOpen : true;
              
              return (
                <Card key={index} className="main-section" data-testid={`menu-category-${index}`}>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-[#D47A2A] text-xl border-b border-[#D47A2A] pb-2 flex-1" data-testid={`menu-category-title-${index}`}>
                        {category.name}
                      </CardTitle>
                      {isCollapsible && (
                        <button
                          onClick={() => {
                            if (isPizzaTomateCategory) setPizzaTomateOpen(!pizzaTomateOpen);
                            if (isPizzaCremeCategory) setPizzaCremeOpen(!pizzaCremeOpen);
                          }}
                          className="ml-2 text-[#D47A2A] hover:text-[#B8661F] transition-colors"
                          data-testid={`toggle-${isPizzaTomateCategory ? 'tomate' : 'creme'}`}
                        >
                          {isOpen ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                    {category.description && (
                      <p className="text-sm text-gray-300 mt-2 italic" data-testid={`menu-category-desc-${index}`}>{category.description}</p>
                    )}
                  </CardHeader>
                  {(!isCollapsible || isOpen) && (
                    <CardContent className="text-white">
                      {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="mb-4 last:mb-0" data-testid={`menu-item-${index}-${itemIndex}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-white" data-testid={`item-name-${index}-${itemIndex}`}>{item.name}</h4>
                          {item.description && (
                            <p className="text-sm text-gray-300 mt-1" data-testid={`item-desc-${index}-${itemIndex}`}>{item.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <span className="text-[#D47A2A] font-bold" data-testid={`item-price-${index}-${itemIndex}`}>{item.price}</span>
                          <div className="flex items-center space-x-1">
                            {getItemQuantity(item, category.name) > 0 && (
                              <button
                                onClick={() => handleRemoveFromCart(item, category.name)}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-1 rounded-full transition-colors"
                                data-testid={`item-minus-btn-${index}-${itemIndex}`}
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                            )}
                            
                            {getItemQuantity(item, category.name) > 0 && (
                              <span className="bg-[#D47A2A] text-white px-2 py-1 rounded-full text-xs font-bold min-w-[24px] text-center" data-testid={`item-qty-${index}-${itemIndex}`}>
                                {getItemQuantity(item, category.name)}
                              </span>
                            )}
                            
                            <button
                              onClick={() => handleAddToCart(item, category.name)}
                              className="bg-[#D47A2A] hover:bg-[#B8661F] text-white p-1 rounded-full transition-colors"
                              data-testid={`item-plus-btn-${index}-${itemIndex}`}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
          </div>
        </div>
      </section>

      {/* Infos Section */}
      <section id="infos" className="py-12 sm:py-16 px-4 sm:px-6" data-testid="infos-section">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-12" data-testid="infos-title">Informations</h2>
          
          {/* Google Maps */}
          <Card className="main-section mb-8">
            <CardContent className="p-0">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2629.4!2d2.2466!3d48.7143!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDQyJzUxLjUiTiAywrAxNCc0Ny44IkU!5e0!3m2!1sfr!2sfr!4v1635000000000!5m2!1sfr!2sfr"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '16px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-maps-iframe"
              ></iframe>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="main-section" data-testid="info-address-card">
              <CardHeader>
                <CardTitle className="text-[#D47A2A] flex items-center" data-testid="info-address-title">
                  <MapPin className="h-6 w-6 mr-2" />
                  Adresse
                </CardTitle>
              </CardHeader>
              <CardContent className="text-white">
                <p data-testid="info-address-text">{mockData.info.address}</p>
                <div className="mt-4">
                  <Button 
                    className="w-full bg-[#D47A2A] hover:bg-[#B8661F]"
                    onClick={() => window.open(mockData.info.directionsLink, '_blank')}
                    data-testid="info-itineraire-btn"
                  >
                    Itinéraire
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="main-section" data-testid="info-horaires-card">
              <CardHeader>
                <CardTitle className="text-[#D47A2A] flex items-center" data-testid="info-horaires-title">
                  <Clock className="h-6 w-6 mr-2" />
                  Horaires
                </CardTitle>
              </CardHeader>
              <CardContent className="text-white">
                <div className="space-y-3">
                  {mockData.horaires.map((horaire, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2" data-testid={`horaire-${idx}`}>
                      <span className="font-medium">{horaire.day}</span>
                      <span className="text-sm text-gray-300 sm:text-white">{horaire.hours}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="main-section" data-testid="info-services-card">
              <CardHeader>
                <CardTitle className="text-[#D47A2A]" data-testid="info-services-title">Services</CardTitle>
              </CardHeader>
              <CardContent className="text-white">
                <div className="space-y-2">
                  {mockData.paymentInfo.map((info, idx) => (
                    <p key={idx} data-testid={`payment-info-${idx}`}>{info}</p>
                  ))}
                </div>
                <Badge className="bg-[#D47A2A] text-white mt-4" data-testid="halal-badge">Halal</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Galerie Section */}
      <section id="galerie" className="py-12 sm:py-16 px-4 sm:px-6" data-testid="galerie-section">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-12" data-testid="galerie-title">Galerie</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {mockData.gallery.map((image, index) => (
              <div 
                key={index} 
                className="cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                onClick={() => openLightbox(image.src)}
                data-testid={`galerie-image-${index}`}
              >
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* À propos Section */}
      <section id="apropos" className="py-12 sm:py-16 px-4 sm:px-6" data-testid="apropos-section">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-12" data-testid="apropos-title">À propos</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.about.map((item, index) => (
              <Card key={index} className="main-section" data-testid={`apropos-card-${index}`}>
                <CardContent className="text-white p-8 text-center">
                  <div className="text-4xl mb-4" data-testid={`apropos-icon-${index}`}>{item.icon}</div>
                  <h3 className="text-xl font-semibold text-[#D47A2A] mb-4" data-testid={`apropos-title-${index}`}>{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed" data-testid={`apropos-desc-${index}`}>{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 px-4 sm:px-6 mb-32 md:mb-0" data-testid="contact-section">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-8 sm:mb-12" data-testid="contact-title">Contact</h2>
          <Card className="main-section max-w-2xl mx-auto">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                <div className="space-y-4">
                  <Button 
                    size="lg" 
                    className="w-full bg-[#D47A2A] hover:bg-[#B8661F] text-white py-4 text-lg"
                    onClick={handlePhoneCall}
                    data-testid="contact-appeler-btn"
                  >
                    <Phone className="h-6 w-6 mr-2" />
                    Appeler maintenant
                  </Button>
                  
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full border-[#D47A2A] text-[#D47A2A] hover:bg-[#D47A2A] hover:text-white py-4 text-lg"
                    onClick={() => window.open(mockData.info.directionsLink, '_blank')}
                    data-testid="contact-itineraire-btn"
                  >
                    <MapPin className="h-6 w-6 mr-2" />
                    Itinéraire
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B1B1B] text-white py-8 px-4 sm:px-6" data-testid="footer">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div data-testid="footer-logo-section">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_woodfiredpizza/artifacts/o98nb6sw_Untitled%20design%20%281%29.png" 
                  alt="Perfecto Pizza Logo" 
                  className="h-12 w-12 object-cover rounded-full"
                  style={{ background: 'transparent' }}
                />
                <h3 className="font-bold text-lg">Perfecto Pizza</h3>
              </div>
              <p data-testid="footer-address">{mockData.info.address}</p>
              <p data-testid="footer-phone">{mockData.info.phone}</p>
            </div>
            <div data-testid="footer-horaires-section">
              <h3 className="font-bold text-lg mb-4">Horaires</h3>
              <p>Lundi → Samedi: 11h00–14h30, 18h00–23h00</p>
              <p>Dimanche: 18h00–23h00</p>
            </div>
            <div data-testid="footer-halal-section">
              <h3 className="font-bold text-lg mb-4">Qualité Halal</h3>
              <p>Toutes nos viandes sont certifiées halal</p>
              <Badge className="bg-[#D47A2A] text-white mt-2">Halal</Badge>
            </div>
          </div>
          <div className="text-center mt-8 pt-8 border-t border-gray-600" data-testid="footer-copyright">
            <p>&copy; 2025 Perfecto Pizza. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Fixed Mobile Cart & Commander Button */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#1B1B1B] border-t border-[#D47A2A] p-4 shadow-2xl" data-testid="mobile-bottom-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2 text-white text-sm">
            <Phone className="h-4 w-4" />
            <span>01 69 19 15 05</span>
          </div>
        </div>
        <div className="w-full">
          <Button 
            onClick={handlePhoneCall} 
            className="w-full bg-[#D47A2A] hover:bg-[#B8661F] text-white py-3 text-sm font-semibold"
            data-testid="mobile-appeler-btn"
          >
            <Phone className="h-4 w-4 mr-1" />
            Appeler
          </Button>
        </div>
      </div>

      {/* Customization Modal */}
      <PerfectoCustomizationModal
        isOpen={customizationModalOpen}
        onClose={() => setCustomizationModalOpen(false)}
        item={selectedItem}
        category={selectedCategory}
        onAddToCart={handleCustomizedAddToCart}
      />

      {/* Promo Modal */}
      <PromoModal
        isOpen={promoModalOpen}
        onClose={() => setPromoModalOpen(false)}
        promoType={selectedPromoType}
        onAddToCart={handlePromoAddToCart}
      />

      {/* TexMex Modal */}
      <TexMexModal
        isOpen={texMexModalOpen}
        onClose={() => setTexMexModalOpen(false)}
        item={selectedItem}
        category={selectedCategory}
        onAddToCart={handleCustomizedAddToCart}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          <div className="relative max-w-4xl max-h-full">
            <img 
              src={lightboxImage} 
              alt="Gallery" 
              className="max-w-full max-h-full object-contain"
              data-testid="lightbox-image"
            />
            <button 
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-4xl hover:text-[#D47A2A]"
              data-testid="lightbox-close-btn"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerfectoHome;
